'use client'

import { useCallback, useEffect, useMemo, useState } from 'react'
import { useAuth } from '@/hooks/use-auth'
import { usePermissions } from '@/hooks/use-permissions'
import { AI_PERMISSIONS } from '@/lib/auth/permissions.constants'
import { Button } from '@/components/ui/button'
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert'
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card'
import {
  Loader2,
  Plus,
  Pencil,
  AlertCircle,
  Zap,
  Settings2,
  Mail,
  MessagesSquare,
  Phone,
  Sparkles,
} from 'lucide-react'
import { toast } from 'sonner'
import { ConfigDialog } from './config-dialog'
import { AIConfig, AIModel, ConfigFormState, AvailableTool } from './types'
import { NO_FALLBACK } from './model-selection'
import { UseCaseRolesSection } from './use-case-roles-section'
import { UseCaseGuardrailSection } from './use-case-guardrail-section'
import { SortableList } from '@/components/ui/sortable-list'
import { useAiUseCasesQuery } from '@/hooks/queries/ai-use-cases'
import { useAiUseCaseRolesQuery } from '@/hooks/queries/ai-use-case-roles'
import type { AiUseCaseView } from '@/server-actions/ai-use-cases'
import type { AiUseCaseRoleView } from '@/server-actions/ai-use-case-roles'
import {
  useUpdateAiUseCaseMutation,
  useReorderAiUseCasesMutation,
} from '@/hooks/mutations/ai-use-cases'
import { USE_CASE_AGENT_GROUPS, type UseCaseAgentGroupKey } from '@/lib/ai/use-cases'
import { AI_USE_CASES } from '@/lib/ai/langchain/types'
import { AGENT_ROLE_USE_CASES } from '@/lib/ai/roles/types'

const GROUP_ICONS: Record<UseCaseAgentGroupKey, React.ReactNode> = {
  email_agent: <Mail className="h-4 w-4 text-gray-500" />,
  message_agent: <MessagesSquare className="h-4 w-4 text-gray-500" />,
  voice_agent: <Phone className="h-4 w-4 text-gray-500" />,
  platform: <Sparkles className="h-4 w-4 text-gray-500" />,
}

/** Use cases whose card shows the Roles section (agent channels, not platform AI). */
const ROLE_ENABLED_USE_CASES = new Set<string>(Object.values(AGENT_ROLE_USE_CASES).flat())

const EMPTY_FORM: ConfigFormState = {
  use_case: '',
  use_case_id: '',
  display_name: '',
  description: '',
  primary_model_id: '',
  fallback_model_id: NO_FALLBACK,
  system_prompt: '',
  prompt_template: '',
  scope: 'global',
  processing_config: {},
  model_config: { primary_json: '{}', fallback_json: '{}' },
}

export function AIConfigPage() {
  const { user, isLoading: authLoading, fetchWithAuth } = useAuth()
  const { can } = usePermissions()
  const [configs, setConfigs] = useState<AIConfig[]>([])
  const [models, setModels] = useState<AIModel[]>([])
  const [tools, setTools] = useState<AvailableTool[]>([])
  const [isLoading, setIsLoading] = useState(true)
  const [isDialogOpen, setIsDialogOpen] = useState(false)
  const [editingConfig, setEditingConfig] = useState<AIConfig | null>(null)
  const [isSaving, setIsSaving] = useState(false)
  const [formData, setFormData] = useState<ConfigFormState>(EMPTY_FORM)

  const hasViewPermission = can(AI_PERMISSIONS.VIEW)
  const hasManagePermission = can(AI_PERMISSIONS.MANAGE_GLOBAL_CONFIGS)

  const useCasesQuery = useAiUseCasesQuery(hasViewPermission)
  const rolesQuery = useAiUseCaseRolesQuery(hasViewPermission)
  const updateUseCase = useUpdateAiUseCaseMutation()
  const reorderUseCases = useReorderAiUseCasesMutation()

  const useCases = useMemo(() => useCasesQuery.data ?? [], [useCasesQuery.data])

  const rolesByUseCase = useMemo(() => {
    const map = new Map<string, AiUseCaseRoleView[]>()
    for (const role of rolesQuery.data ?? []) {
      const list = map.get(role.use_case) ?? []
      list.push(role)
      map.set(role.use_case, list)
    }
    return map
  }, [rolesQuery.data])

  const useCaseBySlug = useMemo(() => {
    const map = new Map<string, AiUseCaseView>()
    for (const uc of useCases) map.set(uc.slug, uc)
    return map
  }, [useCases])

  const configBySlug = useMemo(() => {
    const map = new Map<string, AIConfig>()
    for (const cfg of configs) map.set(cfg.use_case, cfg)
    return map
  }, [configs])

  // Configs whose use_case has no registry row (legacy/removed use cases). Shown
  // separately so drag-reorder stays over the canonical registry only.
  const extraConfigs = useMemo(
    () => configs.filter((cfg) => !useCaseBySlug.has(cfg.use_case)),
    [configs, useCaseBySlug],
  )

  const fetchData = useCallback(async () => {
    try {
      setIsLoading(true)
      const [configsRes, modelsRes, toolsRes] = await Promise.all([
        fetchWithAuth('/api/ai/configs?scope=global'),
        fetchWithAuth('/api/ai/models'),
        fetchWithAuth('/api/ai/tools'),
      ])

      if (!configsRes.ok) throw new Error('Failed to fetch configs')
      if (!modelsRes.ok) throw new Error('Failed to fetch models')

      const configsData = await configsRes.json()
      const modelsData = await modelsRes.json()
      const toolsData = toolsRes.ok ? await toolsRes.json() : { tools: [] }

      setConfigs(configsData.configs || [])
      setModels(modelsData.models || [])
      setTools(toolsData.tools || [])
    } catch (error) {
      console.error('Failed to fetch configs/models:', error)
      toast.error('Failed to load AI configs')
    } finally {
      setIsLoading(false)
    }
  }, [fetchWithAuth])

  useEffect(() => {
    if (!authLoading && hasViewPermission) {
      fetchData()
    }
  }, [authLoading, hasViewPermission, fetchData])

  const openEdit = (config: AIConfig) => {
    const registry = useCaseBySlug.get(config.use_case)
    setEditingConfig(config)
    setFormData({
      use_case: config.use_case || '',
      use_case_id: registry?.id || '',
      display_name: registry?.display_name || config.use_case || '',
      description: registry?.description || '',
      primary_model_id: config.primary_model?.id || '',
      fallback_model_id: config.fallback_model?.id || NO_FALLBACK,
      system_prompt: config.system_prompt || '',
      prompt_template: config.prompt_template || '',
      scope: config.scope || 'global',
      processing_config: config.processing_config || {},
      model_config: {
        primary_json: JSON.stringify(config.model_config?.primary || {}, null, 2),
        fallback_json: JSON.stringify(config.model_config?.fallback || {}, null, 2),
      },
    })
    setIsDialogOpen(true)
  }

  const openAdd = (useCase: AiUseCaseView) => {
    setEditingConfig(null)
    setFormData({
      ...EMPTY_FORM,
      use_case: useCase.slug,
      use_case_id: useCase.id,
      display_name: useCase.display_name,
      description: useCase.description || '',
    })
    setIsDialogOpen(true)
  }

  const handleSave = async () => {
    if (!formData.use_case || !formData.primary_model_id) {
      toast.error('Please select a primary model')
      return
    }


    try {
      setIsSaving(true)

      const hasProcessingConfig = Object.values(formData.processing_config).some(
        (v) => v !== undefined && v !== null,
      )

      let primaryConfig: Record<string, unknown> = {}
      let fallbackConfig: Record<string, unknown> = {}
      try {
        primaryConfig = JSON.parse(formData.model_config.primary_json || '{}')
      } catch {
        toast.error('Invalid JSON in Primary Model Configuration')
        return
      }
      try {
        fallbackConfig = JSON.parse(formData.model_config.fallback_json || '{}')
      } catch {
        toast.error('Invalid JSON in Fallback Model Configuration')
        return
      }

      const modelConfig: Record<string, unknown> = {}
      if (Object.keys(primaryConfig).length > 0) modelConfig.primary = primaryConfig
      if (Object.keys(fallbackConfig).length > 0) modelConfig.fallback = fallbackConfig

      const payload = {
        scope: 'global',
        use_case: formData.use_case,
        primary_model_id: formData.primary_model_id,
        // `null`, not `undefined`: supabase-js serializes the body with JSON.stringify, which
        // drops undefined keys — the column would keep the fallback the operator just removed.
        fallback_model_id:
          formData.fallback_model_id === NO_FALLBACK ? null : formData.fallback_model_id,
        system_prompt: formData.system_prompt || undefined,
        prompt_template: formData.prompt_template || undefined,
        processing_config: hasProcessingConfig ? formData.processing_config : undefined,
        model_config: Object.keys(modelConfig).length > 0 ? modelConfig : undefined,
        is_enabled: true,
      }

      const res = await fetchWithAuth('/api/ai/configs', {
        method: editingConfig ? 'PATCH' : 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      })

      if (!res.ok) {
        const error = await res.json()
        throw new Error(error.error || 'Failed to save configuration')
      }

      // Persist use-case display metadata if it changed (only for registered use
      // cases). Runs as a separate super-user-gated mutation on ai_use_cases.
      const registry = useCaseBySlug.get(formData.use_case)
      const nextName = formData.display_name.trim()
      const nextDescription = formData.description.trim()
      if (
        formData.use_case_id &&
        nextName &&
        (nextName !== (registry?.display_name ?? '') ||
          nextDescription !== (registry?.description ?? ''))
      ) {
        await updateUseCase.mutateAsync({
          id: formData.use_case_id,
          display_name: nextName,
          description: nextDescription || null,
        })
      }

      toast.success('Configuration saved successfully')
      setIsDialogOpen(false)
      fetchData()
    } catch (error) {
      console.error('Failed to save config:', error)
      toast.error(error instanceof Error ? error.message : 'Failed to save configuration')
    } finally {
      setIsSaving(false)
    }
  }

  const availableModels = useMemo(
    () =>
      models.filter(
        (model) => model.is_enabled && model.provider.is_enabled && model.model_type === 'chat',
      ),
    [models],
  )

  // Sections in fixed group order; use cases inside a group keep DB sort_order
  // (useCases arrives sorted). Registered slugs outside any group (shouldn't
  // happen — drift-guarded) fall through to an "Other" section.
  const groupedSections = useMemo(() => {
    const groupedSlugs = new Set<string>()
    const sections = USE_CASE_AGENT_GROUPS.map((group) => {
      const items = useCases.filter((u) => (group.useCases as readonly string[]).includes(u.slug))
      items.forEach((u) => groupedSlugs.add(u.slug))
      return { key: group.key as UseCaseAgentGroupKey | 'other', label: group.label, items }
    })
    const leftover = useCases.filter((u) => !groupedSlugs.has(u.slug))
    if (leftover.length) sections.push({ key: 'other', label: 'Other', items: leftover })
    return sections.filter((s) => s.items.length > 0)
  }, [useCases])

  const renderUseCaseCard = (
    useCase: AiUseCaseView,
    config: AIConfig | undefined,
    dragHandle?: React.ReactNode,
  ) => {
    // voice_agent is registered for role management only: no LangChain model
    // config exists (voice runs on ElevenLabs), so the model panels are hidden.
    const isRolesOnly = useCase.slug === AI_USE_CASES.VOICE_AGENT
    // Agent use cases keep prompts on the agents themselves (seeded from role
    // presets) — the global prompt is unused there and not shown.
    const isAgentUseCase = ROLE_ENABLED_USE_CASES.has(useCase.slug)

    return (
    <Card className="border border-gray-200">
      <CardHeader>
        <div className="flex items-start justify-between gap-2">
          <div className="flex items-start gap-2">
            {dragHandle}
            <div>
              <CardTitle>{useCase.display_name}</CardTitle>
              <CardDescription className="mt-1">
                {useCase.description || <span className="font-mono text-xs">{useCase.slug}</span>}
              </CardDescription>
            </div>
          </div>
          {hasManagePermission && !isRolesOnly && config && (
            <Button variant="ghost" size="sm" onClick={() => openEdit(config)}>
              <Pencil className="h-4 w-4" />
            </Button>
          )}
        </div>
      </CardHeader>
      <CardContent className="space-y-4">
        {isRolesOnly ? null : config ? (
          <>
            <div className="flex items-center justify-between py-3 px-4 bg-gradient-to-r from-blue-50 to-indigo-50 rounded-lg border border-blue-200">
              <div>
                <div className="flex items-center gap-2 mb-1">
                  <Zap className="h-4 w-4 text-blue-600" />
                  <span className="text-sm font-medium text-gray-900">Primary Model</span>
                </div>
                <div className="text-xs text-gray-600">
                  {config.primary_model.provider.display_name}
                </div>
              </div>
              <div className="text-right">
                <div className="text-sm font-semibold text-gray-900">
                  {config.primary_model.display_name}
                </div>
                <div className="text-xs text-gray-500 font-mono">
                  {config.primary_model.model_name}
                </div>
              </div>
            </div>

            {config.fallback_model && (
              <div className="flex items-center justify-between py-2 px-3 bg-gray-50 rounded-lg">
                <span className="text-sm text-gray-700">Fallback Model</span>
                <div className="text-right">
                  <div className="text-sm font-medium text-gray-900">
                    {config.fallback_model.display_name}
                  </div>
                  <div className="text-xs text-gray-500">
                    {config.fallback_model.provider.display_name}
                  </div>
                </div>
              </div>
            )}

            {config.model_config?.primary &&
              Object.keys(config.model_config.primary).length > 0 && (
                <div className="flex items-center justify-between py-2 px-3 bg-gray-50 rounded-lg">
                  <div className="flex items-center gap-2">
                    <Settings2 className="h-3.5 w-3.5 text-gray-500" />
                    <span className="text-sm text-gray-700">Config</span>
                  </div>
                  <span className="text-xs font-mono text-gray-600">
                    {Object.keys(config.model_config.primary).join(', ')}
                  </span>
                </div>
              )}

            {config.system_prompt && !isAgentUseCase && (
              <div className="py-2 px-3 bg-gray-50 rounded-lg">
                <div className="text-sm text-gray-700 mb-1">System Prompt</div>
                <div className="text-xs text-gray-600 font-mono whitespace-pre-wrap line-clamp-2">
                  {config.system_prompt}
                </div>
              </div>
            )}
          </>
        ) : (
          <div className="flex items-center justify-between py-3 px-4 bg-gray-50 rounded-lg border border-dashed border-gray-300">
            <span className="text-sm text-gray-500">No model configured — uses no default.</span>
            {hasManagePermission && (
              <Button variant="outline" size="sm" className="gap-1" onClick={() => openAdd(useCase)}>
                <Plus className="h-3.5 w-3.5" />
                <span>Add</span>
              </Button>
            )}
          </div>
        )}

        {/* Registry rows only — an unregistered use case has no row to hang a guardrail on. */}
        {useCaseBySlug.has(useCase.slug) && (
          <UseCaseGuardrailSection useCase={useCase} canManage={hasManagePermission} />
        )}

        {ROLE_ENABLED_USE_CASES.has(useCase.slug) && (
          <UseCaseRolesSection
            useCaseSlug={useCase.slug}
            useCaseName={useCase.display_name}
            roles={rolesByUseCase.get(useCase.slug) ?? []}
            canManage={hasManagePermission}
          />
        )}
      </CardContent>
    </Card>
    )
  }

  if (authLoading) {
    return (
      <div className="p-6">
        <div className="max-w-5xl mx-auto">
          <div className="animate-pulse space-y-4">
            <div className="h-8 bg-gray-200 rounded w-48"></div>
            <div className="h-64 bg-gray-200 rounded w-full"></div>
          </div>
        </div>
      </div>
    )
  }

  if (!user || !hasViewPermission) {
    return (
      <div className="p-6">
        <div className="max-w-5xl mx-auto">
          <Alert variant="destructive">
            <AlertTitle>Access Denied</AlertTitle>
            <AlertDescription>
              You don't have permission to view AI configurations. Contact your administrator for
              access.
            </AlertDescription>
          </Alert>
        </div>
      </div>
    )
  }

  const showLoader = isLoading || useCasesQuery.isLoading

  return (
    <div className="p-6">
      <div className="max-w-5xl mx-auto space-y-6">
        <div>
          <h1 className="text-2xl font-semibold text-foreground">AI Configurations</h1>
          <p className="text-gray-600 mt-1">
            Default model for each AI use case. Agents fall back to these unless overridden per
            agent.
          </p>
        </div>

        <Alert>
          <AlertCircle className="h-4 w-4" />
          <AlertTitle>Note</AlertTitle>
          <AlertDescription>
            <span>Use cases map to application features (email replies, classification, knowledge base,
            etc.).</span><span>{hasManagePermission && ' Drag cards to reorder; edit a card to rename it.'}</span>
          </AlertDescription>
        </Alert>

        {showLoader && (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="h-8 w-8 animate-spin text-gray-500" />
          </div>
        )}

        {!showLoader &&
          groupedSections.map((section) => (
            <section key={section.key} className="space-y-3">
              <div className="flex items-center gap-2">
                {section.key !== 'other' && GROUP_ICONS[section.key]}
                <h2 className="text-sm font-semibold text-gray-500 uppercase tracking-wide">
                  {section.label}
                </h2>
              </div>
              <SortableList
                ids={section.items.map((u) => u.id)}
                enabled={hasManagePermission && section.items.length > 1}
                className="space-y-4"
                onReorder={(newOrder) => reorderUseCases.mutate(newOrder)}
                renderItem={(id, handle) => {
                  const uc = section.items.find((u) => u.id === id)
                  if (!uc) return null
                  return renderUseCaseCard(uc, configBySlug.get(uc.slug), handle)
                }}
              />
            </section>
          ))}

        {!showLoader && extraConfigs.length > 0 && (
          <div className="space-y-4">
            <h2 className="text-sm font-semibold text-gray-500 uppercase tracking-wide">
              Unregistered use cases
            </h2>
            <div className="space-y-4">
              {extraConfigs.map((config) => {
                const pseudo: AiUseCaseView = {
                  id: config.id,
                  slug: config.use_case,
                  display_name: config.use_case,
                  description: null,
                  sort_order: 0,
                  is_system: false,
                  has_guardrail: false,
                }
                return <div key={config.id}>{renderUseCaseCard(pseudo, config)}</div>
              })}
            </div>
          </div>
        )}
      </div>

      <ConfigDialog
        open={isDialogOpen}
        onOpenChange={setIsDialogOpen}
        formData={formData}
        onChange={setFormData}
        onSave={handleSave}
        isSaving={isSaving}
        editingConfig={editingConfig}
        availableModels={availableModels}
        availableTools={tools}
      />
    </div>
  )
}
